import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Link, IconButton, Snackbar, Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  getSprintHealthTeams, getSprintHealthSprints, getCommitmentRows, addCommitment, removeCommitment, refreshCommitments,
} from '../../../services/sprintHealthService';
import { isAuthenticated } from '../../../services/authService';
import { Section, Pill, Caption, SortHeader, headCellSx, T } from './shared';

const ADO_BASE = 'https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit';
const DONE = ['Done', 'Closed', 'Resolved', 'Completed'];
const ACTIVE = ['Active', 'In Progress', 'Committed', 'Approved'];
const STATE_ORDER = { New: 0, Approved: 1, Committed: 2, Active: 2, 'In Progress': 2, Done: 3, Closed: 3, Resolved: 3, Completed: 3 };
const TYPE_SHORT = { 'Product Backlog Item': 'PBI' };

const stateTone = (s) => (DONE.includes(s) ? 'ok' : ACTIVE.includes(s) ? 'warn' : 'none');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const COLUMNS = [
  ['adoId', 'ID'], ['title', 'Title'], ['team', 'Team'], ['state', 'Status'],
  ['targetDate', 'Target date'], ['totalStoryPoints', 'Total SP'], ['completionPct', 'Completion'],
];
const NUMERIC = ['totalStoryPoints', 'completionPct'];

/** Current quarter and the next one, plus every quarter that has sprints. Newest first. */
function quarterOptions(sprints) {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  const keys = new Set([`${now.getFullYear()}|Q${q}`, q === 4 ? `${now.getFullYear() + 1}|Q1` : `${now.getFullYear()}|Q${q + 1}`]);
  sprints.forEach((s) => s.year && s.quarter && keys.add(`${s.year}|${s.quarter}`));
  return [...keys].sort().reverse().map((k) => { const [year, quarter] = k.split('|'); return { key: k, year, quarter, label: `${quarter} ${year}` }; });
}

const QuarterlyCommitmentTab = () => {
  const [teams, setTeams] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [quarterKey, setQuarterKey] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addId, setAddId] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sort, setSort] = useState({ key: 'team', dir: 1 });
  const [toast, setToast] = useState('');
  const loggedIn = isAuthenticated();

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([getSprintHealthTeams(), getSprintHealthSprints()]);
        setTeams(t.data || []);
        const opts = quarterOptions(s.data || []);
        setQuarters(opts);
        const now = new Date();
        setQuarterKey(`${now.getFullYear()}|Q${Math.floor(now.getMonth() / 3) + 1}`);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, []);

  const quarter = quarters.find((q) => q.key === quarterKey);
  const teamName = useCallback((id) => teams.find((t) => t.id === id)?.displayName || id, [teams]);

  const load = useCallback(async () => {
    if (!quarter) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getCommitmentRows(quarter.year, quarter.quarter);
      setRows(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [quarter]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const v = (r) => {
      switch (sort.key) {
        case 'team': return teamName(r.team).toLowerCase();
        case 'state': return STATE_ORDER[r.state] ?? 9;
        case 'adoId': return Number(r.adoId);
        case 'totalStoryPoints': return r.totalStoryPoints || 0;
        case 'completionPct': return r.completionPct || 0;
        case 'targetDate': return r.targetDate ? new Date(r.targetDate).getTime() : Infinity;
        default: return String(r[sort.key] || '').toLowerCase();
      }
    };
    return rows
      .filter((r) => teamFilter === 'all' || r.team === teamFilter)
      .sort((a, b) => {
        const x = v(a); const y = v(b);
        const c = x < y ? -1 : x > y ? 1 : 0;
        return c * sort.dir || String(a.targetDate || '').localeCompare(String(b.targetDate || '')) || a.adoId.localeCompare(b.adoId);
      });
  }, [rows, teamFilter, sort, teamName]);

  const totalSP = visible.reduce((s, r) => s + (r.totalStoryPoints || 0), 0);

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: NUMERIC.includes(key) ? -1 : 1 }));

  const submit = async (e) => {
    e.preventDefault();
    const id = addId.trim().replace(/^#/, '');
    if (!/^\d{3,9}$/.test(id)) { setAddError('Enter a numeric ADO ID, for example 935210.'); return; }
    setAdding(true);
    setAddError(null);
    try {
      const res = await addCommitment(quarter.year, quarter.quarter, id);
      setAddId('');
      setToast(`#${id} added to ${teamName(res.data?.team)}'s ${quarter.label} commitments`);
      await load();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await refreshCommitments(quarter.year, quarter.quarter, teamFilter === 'all' ? null : teamFilter);
      const { refreshed, moved, missing } = res.data || {};
      const parts = [`${refreshed} item${refreshed === 1 ? '' : 's'} refreshed from ADO`];
      if (moved?.length) parts.push(`${moved.length} moved to a different team`);
      if (missing?.length) parts.push(`not found in ADO: ${missing.map((x) => `#${x}`).join(', ')}`);
      setToast(parts.join(' · '));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const lastRefreshed = rows.reduce((m, r) => (r.refreshedAt && (!m || r.refreshedAt < m) ? r.refreshedAt : m), null);

  const remove = async (r) => {
    try {
      await removeCommitment(quarter.year, quarter.quarter, r.team, r.adoId);
      setConfirmRemove(null);
      setToast(`#${r.adoId} removed from ${quarter.label} commitments`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="qc-quarter-label">Quarter</InputLabel>
          <Select labelId="qc-quarter-label" id="qc-quarter" label="Quarter" value={quarter ? quarterKey : ''} onChange={(e) => setQuarterKey(e.target.value)}>
            {quarters.map((q) => <MenuItem key={q.key} value={q.key}>{q.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Caption>Items added after the first two weeks of the quarter are marked as added mid-quarter.</Caption>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {loggedIn ? (
        <Section title="Add a commitment" desc="Enter an ADO Epic, Feature or PBI ID. Details are pulled from ADO, and the team comes from the item's Team field in ADO.">
          <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField id="qc-ado-id" size="small" label="ADO ID" placeholder="e.g. 935210" value={addId} onChange={(e) => setAddId(e.target.value)} inputProps={{ inputMode: 'numeric', autoComplete: 'off' }} sx={{ width: 200 }} />
            <Button type="submit" variant="contained" disabled={adding || !quarter}>{adding ? 'Adding…' : 'Add'}</Button>
          </Box>
          {addError && <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setAddError(null)}>{addError}</Alert>}
        </Section>
      ) : (
        <Alert severity="info">Sign in to add or remove commitments.</Alert>
      )}

      <Section
        title={`${quarter?.label || ''} commitments`}
        desc={`${visible.length} item${visible.length === 1 ? '' : 's'} · ${totalSP} story points across descendants`}
        right={(
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {loggedIn && (
              <Tooltip title="Re-read title, status, assignee, target date, team and story points from ADO">
                <span>
                  <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={refresh} disabled={refreshing || !rows.length}>
                    {refreshing ? 'Refreshing…' : 'Refresh from ADO'}
                  </Button>
                </span>
              </Tooltip>
            )}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="qc-team-label">Team</InputLabel>
            <Select
              labelId="qc-team-label"
              id="qc-team"
              label="Team"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <MenuItem value="all">All teams</MenuItem>
              {teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.displayName}</MenuItem>)}
            </Select>
          </FormControl>
          </Box>
        )}
      >
        {loading && !rows.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No commitments for {quarter?.label}{teamFilter !== 'all' ? ' for this team' : ''} yet. Add an ADO ID above.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {COLUMNS.map(([key, label]) => (
                    <TableCell
                      key={key}
                      align={NUMERIC.includes(key) ? 'right' : 'left'}
                      sx={headCellSx}
                      aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
                    >
                      <SortHeader label={label} active={sort.key === key} dir={sort.dir} onClick={() => onSort(key)} align={NUMERIC.includes(key) ? 'right' : 'left'} />
                    </TableCell>
                  ))}
                  <TableCell sx={headCellSx}><Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Remove</Box></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={`${r.team}-${r.adoId}`} hover>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      <Link href={`${ADO_BASE}/${r.adoId}`} target="_blank" rel="noopener" sx={{ color: T.green, fontWeight: 600 }}>{r.adoId}</Link>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: T.muted, mr: 0.75 }}>{TYPE_SHORT[r.workItemType] || r.workItemType}</Box>
                      {r.title}
                      {r.addedLate && <Box component="span" sx={{ ml: 1 }}><Pill tone="warn">Added {fmtDate(r.addedAt)}</Pill></Box>}
                    </TableCell>
                    <TableCell>{teamName(r.team)}</TableCell>
                    <TableCell>{r.state ? <Pill tone={stateTone(r.state)}>{r.state}</Pill> : '—'}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtDate(r.targetDate)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.totalStoryPoints}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 150 }}>
                      <Tooltip title={`${r.completedStoryPoints ?? 0} of ${r.totalStoryPoints ?? 0} story points closed`}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 70, height: 8, borderRadius: 4, bgcolor: 'rgba(26,46,34,0.06)', overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.min(100, r.completionPct || 0)}%`, height: '100%', bgcolor: (r.completionPct || 0) >= 100 ? '#2E7D32' : T.green }} />
                          </Box>
                          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' }}>{r.completionPct ?? 0}%</Box>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {loggedIn && (confirmRemove === `${r.team}-${r.adoId}` ? (
                        <Typography variant="body2" component="span">
                          Remove? <Link component="button" onClick={() => remove(r)}>Yes</Link> <Link component="button" onClick={() => setConfirmRemove(null)}>No</Link>
                        </Typography>
                      ) : (
                        <Tooltip title={`Remove #${r.adoId}`}>
                          <IconButton size="small" aria-label={`Remove ${r.adoId}`} onClick={() => setConfirmRemove(`${r.team}-${r.adoId}`)}><CloseIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Caption sx={{ mt: 1.5 }}>
          ID, title, status, target date and team come from the ADO work item. Total SP is the sum of story points on all its descendants; Completion is the share of those points on closed items. Sprint Review uses this list to work out alignment.
          {lastRefreshed ? ` Details last refreshed from ADO ${new Date(lastRefreshed).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}.` : ''}
        </Caption>
      </Section>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default QuarterlyCommitmentTab;
